import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { PaymentService, TransactionStatus } from '../../../core/services/payment.service';
import { StorageService } from '../../../core/services/storage.service';

type PageState = 'loading' | 'success' | 'failure' | 'pending' | 'no-transaction';

@Component({
  selector: 'app-payment-return',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="return-page">
      <header class="header">
        <div class="header-inner">
          <a routerLink="/" class="logo">
            <img src="assets/images/logo%20kia%20ora.png" alt="Serenity Hotel" class="logo-img" />
          </a>
        </div>
      </header>

      <main class="main">
        <div class="card">

          <!-- Chargement / polling -->
          @if (state === 'loading' || state === 'pending') {
            <div class="state-icon pending-icon">
              <div class="spinner"></div>
            </div>
            <h1 class="title">Vérification du paiement</h1>
            <p class="subtitle">
              @if (state === 'pending') {
                Votre paiement est en cours de confirmation. Veuillez patienter…
              } @else {
                Connexion à FedaPay en cours…
              }
            </p>
            @if (pollCount > 0) {
              <p class="poll-info">Tentative {{ pollCount }} / {{ maxPolls }}</p>
            }
          }

          <!-- Succès -->
          @if (state === 'success') {
            <div class="state-icon success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h1 class="title success">Paiement confirmé !</h1>
            <p class="subtitle">
              Votre réservation est confirmée. Merci pour votre confiance.
            </p>
            @if (transactionStatus) {
              <div class="amount-box">
                <span class="amount-label">Montant payé</span>
                <span class="amount-value">{{ transactionStatus.montant | number:'1.0-0' }} FCFA</span>
              </div>
            }
            <p class="redirect-info">Redirection vers votre profil dans {{ redirectCountdown }}s…</p>
            <a routerLink="/profile" class="btn btn-primary">Voir mes réservations</a>
          }

          <!-- Échec / Annulé -->
          @if (state === 'failure') {
            <div class="state-icon failure-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h1 class="title failure">Paiement non abouti</h1>
            <p class="subtitle">
              @if (transactionStatus?.paiementStatut === 'ANNULE') {
                Vous avez annulé le paiement. Votre réservation reste en attente.
              } @else {
                Le paiement a échoué. Aucun montant n'a été débité.
              }
            </p>
            <p class="retry-info">Rendez-vous dans votre profil pour réessayer le paiement.</p>
            <a routerLink="/profile" class="btn btn-primary">Retour au profil</a>
          }

          <!-- Transaction introuvable -->
          @if (state === 'no-transaction') {
            <div class="state-icon failure-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h1 class="title failure">Session expirée</h1>
            <p class="subtitle">Impossible de retrouver la transaction. Consultez vos réservations.</p>
            <a routerLink="/profile" class="btn btn-primary">Mes réservations</a>
          }

          <!-- Timeout polling -->
          @if (state === 'loading' && pollCount >= maxPolls) {
            <p class="timeout-info">
              La confirmation prend plus de temps que prévu. Vérifiez votre profil dans quelques minutes.
            </p>
            <a routerLink="/profile" class="btn btn-secondary">Voir mon profil</a>
          }

        </div>
      </main>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Jost:wght@300;400;500;600;700&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    :host {
      --burgundy:    #3D1020;
      --burgundy-dk: #2A0916;
      --gold:        #F8B414;
      --gold-dk:     #D99A0A;
      --cream:       #FAF0D7;
      --border:      #E8D8B4;
      --green:       #2e7d32;
      --shadow:      0 4px 24px rgba(61,16,32,.12);
    }

    .return-page {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      background: var(--cream);
      font-family: 'Jost', sans-serif;
    }

    .header {
      background: white;
      border-bottom: 1px solid var(--border);
      padding: 0 20px;
    }

    .header-inner {
      max-width: 1200px;
      margin: 0 auto;
      height: 68px;
      display: flex;
      align-items: center;
    }

    .logo-img { height: 44px; width: auto; }

    .main {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
    }

    .card {
      background: white;
      border-radius: 8px;
      box-shadow: var(--shadow);
      padding: 52px 48px;
      max-width: 480px;
      width: 100%;
      text-align: center;
    }

    .state-icon {
      width: 88px;
      height: 88px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 28px;
    }

    .pending-icon {
      background: rgba(248,180,20,.12);
      border: 2px solid var(--gold);
    }

    .success-icon {
      background: rgba(46,125,50,.1);
      border: 2px solid var(--green);
      color: var(--green);
    }

    .failure-icon {
      background: rgba(61,16,32,.08);
      border: 2px solid var(--burgundy);
      color: var(--burgundy);
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid var(--border);
      border-top-color: var(--gold-dk);
      border-radius: 50%;
      animation: spin .9s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    .title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 30px;
      font-weight: 700;
      color: var(--burgundy);
      margin-bottom: 12px;
    }

    .title.success { color: var(--green); }
    .title.failure { color: var(--burgundy); }

    .subtitle {
      color: #7A5A65;
      font-size: 15px;
      line-height: 1.6;
      margin-bottom: 24px;
    }

    .amount-box {
      background: var(--cream);
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 16px 24px;
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .amount-label { font-size: 13px; color: #7A5A65; }

    .amount-value {
      font-family: 'Cormorant Garamond', serif;
      font-size: 24px;
      font-weight: 700;
      color: var(--green);
    }

    .redirect-info {
      font-size: 13px;
      color: #7A5A65;
      margin-bottom: 20px;
    }

    .poll-info {
      font-size: 12px;
      color: #7A5A65;
      margin-bottom: 16px;
    }

    .retry-info, .timeout-info {
      font-size: 14px;
      color: #7A5A65;
      margin-bottom: 20px;
      line-height: 1.5;
    }

    .btn {
      display: inline-block;
      padding: 13px 32px;
      border-radius: 4px;
      font-size: 15px;
      font-weight: 600;
      font-family: 'Jost', sans-serif;
      text-decoration: none;
      letter-spacing: .04em;
      cursor: pointer;
      transition: background .2s, color .2s;
    }

    .btn-primary {
      background: var(--burgundy);
      color: var(--cream);
    }

    .btn-primary:hover { background: var(--burgundy-dk); }

    .btn-secondary {
      background: var(--gold);
      color: var(--burgundy);
    }

    .btn-secondary:hover { background: var(--gold-dk); }

    @media (max-width: 560px) {
      .card { padding: 36px 24px; }
    }
  `],
})
export class PaymentReturnComponent implements OnInit, OnDestroy {
  state: PageState = 'loading';
  transactionStatus: TransactionStatus | null = null;
  pollCount = 0;
  readonly maxPolls = 10;
  redirectCountdown = 5;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private isBrowser: boolean;

  constructor(
    private paymentService: PaymentService,
    private storageService: StorageService,
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    const raw = this.storageService.get(PaymentService.TRANSACTION_KEY);
    const transactionId = raw ? parseInt(raw, 10) : null;

    if (!transactionId || isNaN(transactionId)) {
      this.state = 'no-transaction';
      return;
    }

    this.fetchStatus(transactionId);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  private fetchStatus(transactionId: number): void {
    this.paymentService.checkStatus(transactionId).subscribe({
      next: (status) => this.handleStatus(status, transactionId),
      error: () => this.scheduleRetry(transactionId),
    });
  }

  private handleStatus(status: TransactionStatus, transactionId: number): void {
    this.transactionStatus = status;

    switch (status.paiementStatut) {
      case 'EFFECTUE':
        this.storageService.remove(PaymentService.TRANSACTION_KEY);
        this.state = 'success';
        this.startRedirectCountdown();
        break;

      case 'ECHOUE':
      case 'ANNULE':
        this.state = 'failure';
        break;

      case 'EN_ATTENTE':
      case 'PARTIELLEMENT_PAYEE':
      default:
        this.state = 'pending';
        this.scheduleRetry(transactionId);
        break;
    }
  }

  private scheduleRetry(transactionId: number): void {
    if (this.pollCount >= this.maxPolls) {
      this.state = 'pending';
      return;
    }
    this.pollCount++;
    this.pollTimer = setTimeout(() => this.fetchStatus(transactionId), 2000);
  }

  private startRedirectCountdown(): void {
    this.countdownTimer = setInterval(() => {
      this.redirectCountdown--;
      if (this.redirectCountdown <= 0) {
        clearInterval(this.countdownTimer!);
        this.router.navigate(['/profile']);
      }
    }, 1000);
  }
}
